/**
 * Company form defaults — shared between the read (hydrate) and write
 * (diff-to-payload) sides.
 *
 * Mirrors `apps/web/src/features/customers/lib/form-defaults.ts`,
 * adapted for the company shape. RHF needs controlled `''` defaults; the
 * wire prefers `undefined` for "not set" — we translate at the seam.
 */
import type { z } from "zod";
import type { companies } from "@flint/api-contract";

type CompanyRow = z.infer<typeof companies.getOutput>;
type CompanyUpdate = z.infer<typeof companies.updateInput>;

/**
 * Form-side value shape. Every field is a non-undefined string so RHF's
 * controlled inputs stay happy. The submit handler diffs + strips empty
 * optionals before posting.
 */
export interface CompanyFormValues {
  legalName: string;
  tradeName: string;
  email: string;
  phone: string;
  address: string;
  city: string;
  state: string;
  pincode: string;
  type: string;
  /** `from_customer | always_sender_state | ask_per_invoice` — invoice
   *  composer behaviour for place-of-supply (migration 0044). */
  defaultPlaceOfSupply: string;
  registeredOn: string;
  accentColour: string;
  pan: string;
  cin: string;
  gstin: string;
  /**
   * Bank details printed on the customer-facing invoice PDF (PR-H). Public
   * to the customer — they need them to pay. Plain text; not encrypted.
   * UPI takes priority on the printed invoice; IFSC + account number are
   * the fallback for NEFT/RTGS.
   */
  bankName: string;
  bankAccountNumber: string;
  bankIfsc: string;
  bankUpiId: string;
  /** Export statutory credentials (migration 0115) — IEC / AEPC RCMC no. /
   *  AD-bank code / LUT no. Required on export paperwork. */
  iec: string;
  aepcRcmcNo: string;
  adCode: string;
  lutNo: string;
  /** Boilerplate Declaration text printed on every invoice PDF
   *  (migration 0035). Operator overrides per invoice in the composer. */
  defaultInvoiceDeclaration: string;
}

export const EMPTY_FORM: CompanyFormValues = {
  legalName: "",
  tradeName: "",
  email: "",
  phone: "",
  address: "",
  city: "",
  state: "",
  pincode: "",
  type: "Regular",
  defaultPlaceOfSupply: "from_customer",
  registeredOn: "",
  accentColour: "sky",
  pan: "",
  cin: "",
  gstin: "",
  bankName: "",
  bankAccountNumber: "",
  bankIfsc: "",
  bankUpiId: "",
  iec: "",
  aepcRcmcNo: "",
  adCode: "",
  lutNo: "",
  defaultInvoiceDeclaration: "",
};

/** Hydrate the form from a server-fetched company row. */
export function formFromCompany(c: CompanyRow): CompanyFormValues {
  return {
    legalName: c.legalName,
    tradeName: c.tradeName ?? "",
    email: c.email ?? "",
    phone: c.phone ?? "",
    address: c.address ?? "",
    city: c.city ?? "",
    state: c.state ?? "",
    pincode: c.pincode ?? "",
    type: c.type,
    defaultPlaceOfSupply: c.defaultPlaceOfSupply || "from_customer",
    registeredOn: c.registeredOn ?? "",
    accentColour: c.accentColour ?? "sky",
    pan: c.pan ?? "",
    cin: c.cin ?? "",
    gstin: c.gstin ?? "",
    bankName: c.bankName ?? "",
    bankAccountNumber: c.bankAccountNumber ?? "",
    bankIfsc: c.bankIfsc ?? "",
    bankUpiId: c.bankUpiId ?? "",
    iec: c.iec ?? "",
    aepcRcmcNo: c.aepcRcmcNo ?? "",
    adCode: c.adCode ?? "",
    lutNo: c.lutNo ?? "",
    defaultInvoiceDeclaration:
      (c as { defaultInvoiceDeclaration?: string | null }).defaultInvoiceDeclaration ?? "",
  };
}

/**
 * Patch builder — keeps the `set<K>` cast in ONE place (TS can't narrow
 * a `Partial<{...}>` from a generic key in a loop without help). The
 * runtime behaviour is identical to direct assignment; this just removes
 * 8 inline `(patch as Record<string, unknown>)[key] = …` casts.
 */
function makePatchBuilder(): {
  patch: CompanyUpdate;
  set: <K extends keyof CompanyUpdate>(key: K, value: CompanyUpdate[K]) => void;
} {
  const patch: CompanyUpdate = {};
  return {
    patch,
    set(key, value) {
      patch[key] = value;
    },
  };
}

/** Optional text columns where the empty string round-trips to null. */
const NULLABLE_TEXT_KEYS = [
  "tradeName",
  "email",
  "phone",
  "address",
  "city",
  "pincode",
  "registeredOn",
  "bankName",
  "bankAccountNumber",
  "bankIfsc",
  "bankUpiId",
  "iec",
  "aepcRcmcNo",
  "adCode",
  "lutNo",
  "defaultInvoiceDeclaration",
] as const satisfies ReadonlyArray<keyof CompanyFormValues>;

/** Locked-once-set columns — first-time entry permitted, mutations blocked. */
const LOCKED_KEYS = ["pan", "cin", "gstin"] as const satisfies ReadonlyArray<
  keyof CompanyFormValues
>;

/**
 * Build an update payload by diffing against the baseline. Empty-string
 * on optional text fields is sent as `''` — the server normalises to
 * NULL via the "empty string clears" convention in `update-company.ts`.
 *
 * Locked-once-set fields (PAN, CIN, GSTIN) are stripped client-side
 * when baseline already has a value — the server enforces it as the
 * floor (CompanyLockedFieldError → 409).
 *
 * Returns `null` when nothing changed — caller short-circuits without
 * a wire round-trip.
 */
export function valuesToUpdatePayload(
  v: CompanyFormValues,
  base: CompanyFormValues,
): CompanyUpdate | null {
  const { patch, set } = makePatchBuilder();

  // Required text — only legalName.
  if (v.legalName.trim() !== base.legalName.trim()) {
    set("legalName", v.legalName.trim());
  }

  // Nullable text fields — send the trimmed value or '' to clear.
  for (const key of NULLABLE_TEXT_KEYS) {
    if (v[key].trim() !== base[key].trim()) {
      // The empty string flows through as "clear to null" — Zod's
      // union(string, '') accepts both.
      set(key, v[key].trim());
    }
  }

  // Enums — only set when actually changed; never send empty strings
  // (the wire schema would reject them).
  if (v.state && v.state !== base.state) {
    set("state", v.state as CompanyUpdate["state"]);
  }
  if (v.type && v.type !== base.type) {
    set("type", v.type as CompanyUpdate["type"]);
  }
  if (v.defaultPlaceOfSupply && v.defaultPlaceOfSupply !== base.defaultPlaceOfSupply) {
    set("defaultPlaceOfSupply", v.defaultPlaceOfSupply as CompanyUpdate["defaultPlaceOfSupply"]);
  }
  if (v.accentColour && v.accentColour !== base.accentColour) {
    set("accentColour", v.accentColour as CompanyUpdate["accentColour"]);
  }

  // Locked-once-set fields. Only include when (a) baseline is empty AND
  // (b) the user typed something. Otherwise the server would reject with
  // CompanyLockedFieldError → 409 — better to fail fast in the SPA.
  for (const key of LOCKED_KEYS) {
    if (!base[key].trim() && v[key].trim() !== base[key].trim()) {
      set(key, v[key].trim().toUpperCase());
    }
  }

  return Object.keys(patch).length === 0 ? null : patch;
}

/**
 * Compute whether the form is dirty (any field !== baseline).
 *
 * Used for the SaveBar's status, separate from RHF's `formState.isDirty`
 * because RHF treats trimmed/untrimmed differently and we want the
 * user's perception of "did I actually change anything".
 */
export function isDirtyVs(v: CompanyFormValues, base: CompanyFormValues): boolean {
  return (Object.keys(EMPTY_FORM) as Array<keyof CompanyFormValues>).some(
    (key) => v[key].trim() !== base[key].trim(),
  );
}
